import { useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  Divider, Grid2 as Grid, IconButton, InputAdornment, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  packagesApi, insuranceSchemesApi, currenciesApi,
  type PackageDto, type PackageComponentDto, type PackageType,
  type SkippedComponentPolicy, type ExtraComponentPolicy,
  type UpsertPackageRequest, type ItemPickerRow, type ServiceDto,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { CatalogItemPicker } from "@/components/Pickers";
import { ServicePicker } from "@/components/ServicePicker";
import { PageHeader } from "@/components/PageHeader";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { RowActions } from "@/components/RowActions";
import { StatusPill } from "@/components/StatusPill";
import { useAuth } from "@/store/auth";
import { todayIso } from "@/shared/datetime";

const today = todayIso;
const money = (n?: number | null) =>
  n == null ? "—" : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TYPE_LABEL: Record<PackageType, string> = {
  HealthCheck: "Health check",
  Maternity: "Maternity",
  Surgical: "Surgical",
  DayCare: "Day care",
  Other: "Other",
};

const SKIPPED_LABEL: Record<SkippedComponentPolicy, string> = {
  NoRefund: "Price stands",
  ProRataCredit: "Pro-rata credit",
};

const EXTRA_LABEL: Record<ExtraComponentPolicy, string> = {
  ChargeSeparately: "Billed separately",
  IncludedNoCharge: "Covered by the package",
};

/**
 * Packages (W18) — a fixed price covering a defined basket of services and stock items.
 *
 * <p>The screen is built around the one thing a package is for: the price is <em>not</em> the sum
 * of its parts, and the gap between them is the offer. So the component editor shows what each
 * part costs on its own and totals it against the package price, because a package quietly priced
 * above its own components is the most common way this master goes wrong.</p>
 */
export function PackagesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<PackageType | "">("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [editing, setEditing] = useState<PackageDto | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [cloneFrom, setCloneFrom] = useState<PackageDto | null>(null);

  const canManage = useAuth(s => s.hasPermission("pricing.package.manage"));

  const query = useQuery({
    queryKey: ["packages", search, typeFilter, pagination.page, pagination.pageSize],
    queryFn: () => packagesApi.list({
      search: search || undefined,
      packageType: typeFilter || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "Package deleted" },
    mutationFn: (id: string) => packagesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["packages"] }),
  });

  const columns: GridColDef<PackageDto>[] = [
    { field: "code", headerName: "Code", width: 140 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
    {
      field: "packageType", headerName: "Kind", width: 130,
      renderCell: (p) => <Chip size="small" variant="outlined" label={TYPE_LABEL[p.row.packageType]} />,
    },
    {
      field: "price", headerName: "Price", width: 130, align: "right", headerAlign: "right",
      renderCell: (p) => `${p.row.currencyCode ? `${p.row.currencyCode} ` : ""}${money(p.row.price)}`,
    },
    {
      field: "componentCount", headerName: "Includes", width: 110, align: "right",
      headerAlign: "right",
      renderCell: (p) => (
        <span>{p.row.componentCount} item{p.row.componentCount === 1 ? "" : "s"}</span>
      ),
    },
    {
      field: "insuranceSchemeName", headerName: "Offered to", width: 160,
      renderCell: (p) => p.row.insuranceSchemeName
        ?? <Box component="span" sx={{ color: "text.disabled" }}>Everyone</Box>,
    },
    {
      field: "policies", headerName: "If skipped / extra", width: 220, sortable: false,
      renderCell: (p) => (
        <Stack spacing={0} sx={{ py: 0.5 }}>
          <Typography variant="caption">{SKIPPED_LABEL[p.row.skippedPolicy]}</Typography>
          <Typography variant="caption" color="text.secondary">
            {EXTRA_LABEL[p.row.extraPolicy]}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "effectiveFrom", headerName: "Effective", width: 180, sortable: false,
      renderCell: (p) => `${p.row.effectiveFrom} → ${p.row.effectiveTo ?? "open"}`,
    },
    {
      field: "isActive", headerName: "Status", width: 110, sortable: false,
      renderCell: (p) => (
        <StatusPill label={p.row.isActive ? "Active" : "Inactive"}
          tone={p.row.isActive ? "success" : "neutral"} />
      ),
    },
    {
      field: "actions", headerName: "", width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <RowActions actions={[
          {
            label: "Edit", icon: <EditIcon fontSize="small" />, hidden: !canManage,
            onClick: () => { setCloneFrom(null); setEditing(p.row); setFormOpen(true); },
          },
          {
            // Packages come in families — the same health check at three price points — and
            // retyping fifteen components for each is how the third one ends up wrong.
            label: "Duplicate", icon: <ContentCopyIcon fontSize="small" />, hidden: !canManage,
            onClick: () => { setEditing(null); setCloneFrom(p.row); setFormOpen(true); },
          },
          {
            label: "Delete", icon: <DeleteIcon fontSize="small" />, color: "error",
            hidden: !canManage,
            onClick: () => {
              if (confirm(`Delete package "${p.row.code}"? A package that has been billed cannot be deleted — deactivate it instead.`)) {
                deleteMutation.mutate(p.row.id);
              }
            },
          },
        ]} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Pricing"]}
        title="Packages"
        subtitle="One fixed price covering a basket of services and items. It bills as ordinary lines with the price spread across them, so revenue still lands where it was earned."
        actions={canManage ? (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => { setEditing(null); setCloneFrom(null); setFormOpen(true); }}>
            New package
          </Button>
        ) : undefined}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        A package price is deliberately <b>not</b> the sum of its parts — the difference is the
        offer. When it is billed, the price is spread across its components in proportion to what
        each would have cost, so the lines always add up to the quoted figure exactly.
      </Alert>

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            placeholder="Search by code or name" fullWidth size="small"
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <TextField select label="Kind" size="small" sx={{ minWidth: 170 }}
            value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as PackageType | "")}>
            <MenuItem value="">All</MenuItem>
            {(Object.keys(TYPE_LABEL) as PackageType[]).map(t => (
              <MenuItem key={t} value={t}>{TYPE_LABEL[t]}</MenuItem>
            ))}
          </TextField>
        </Stack>

        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>
        )}

        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p) => canManage
              && (setCloneFrom(null), setEditing(p.row), setFormOpen(true))}
          />
        </Box>
      </Paper>

      <PackageDialog
        open={formOpen} onClose={() => setFormOpen(false)}
        pkg={editing} cloneFrom={cloneFrom}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------------------------
// Create / edit
// ---------------------------------------------------------------------------------------------

interface DraftComponent {
  key: string;
  lineType: "Service" | "Item" | "Rx";
  service: ServiceDto | null;
  serviceId: string;
  serviceName: string;
  item: ItemPickerRow | null;
  itemId: string;
  itemName: string;
  qty: string;
  /** What it costs on its own, when we know — shown so the basket can be totalled. */
  listRate?: number | null;
}

const blankComponent = (): DraftComponent => ({
  key: crypto.randomUUID(), lineType: "Service",
  service: null, serviceId: "", serviceName: "",
  item: null, itemId: "", itemName: "", qty: "1",
});

/**
 * A saved component names its item but does not carry the catalog row the picker binds to.
 * Rebuild enough of one to render the selection — the id is what gets saved back, and the label
 * is all this screen reads off it. The remaining fields are placeholders; nothing here consults
 * the stock or schedule flags, and the real row replaces this the moment the user picks again.
 *
 * <p>This used to be recovered by searching a preloaded page of items, which meant editing a
 * package whose item sorted past that page showed an empty field and silently dropped the
 * component on save.</p>
 */
const savedItemRow = (c: PackageComponentDto): ItemPickerRow | null =>
  c.itemId
    ? {
        id: c.itemId,
        sku: c.sku ?? "",
        name: c.itemName ?? c.sku ?? "",
        isScheduleDrug: false, isOnFormulary: true,
        availableQty: 0, isOutOfStock: false, isLowStock: false,
      }
    : null;

/** The service-side twin of {@link savedItemRow}; same reasoning, same placeholder fields. */
const savedServiceRow = (c: PackageComponentDto): ServiceDto | null =>
  c.serviceId
    ? {
        id: c.serviceId,
        code: "",
        name: c.serviceName ?? "",
        defaultRate: c.listRate ?? 0,
        currentRate: c.listRate ?? 0,
        gstRate: 0,
        isActive: true,
      }
    : null;

function PackageDialog({
  open, onClose, pkg, cloneFrom,
}: { open: boolean; onClose: () => void; pkg: PackageDto | null; cloneFrom: PackageDto | null }) {
  const qc = useQueryClient();
  const isEdit = !!pkg;
  const source = pkg ?? cloneFrom;

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [packageType, setPackageType] = useState<PackageType>("HealthCheck");
  const [price, setPrice] = useState("0");
  const [currencyId, setCurrencyId] = useState("");
  const [insuranceSchemeId, setInsuranceSchemeId] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [effectiveTo, setEffectiveTo] = useState("");
  const [skippedPolicy, setSkippedPolicy] = useState<SkippedComponentPolicy>("NoRefund");
  const [extraPolicy, setExtraPolicy] = useState<ExtraComponentPolicy>("ChargeSeparately");
  const [maxPerEpisode, setMaxPerEpisode] = useState("1");
  const [isActive, setIsActive] = useState(true);
  const [components, setComponents] = useState<DraftComponent[]>([blankComponent()]);

  useEffect(() => {
    if (!open) return;
    setCode(pkg?.code ?? "");
    setName(source ? (isEdit ? source.name : `${source.name} (copy)`) : "");
    setDescription(source?.description ?? "");
    setPackageType(source?.packageType ?? "HealthCheck");
    setPrice(String(source?.price ?? 0));
    setCurrencyId(source?.currencyId ?? "");
    setInsuranceSchemeId(source?.insuranceSchemeId ?? "");
    setEffectiveFrom(source?.effectiveFrom ?? today());
    setEffectiveTo(source?.effectiveTo ?? "");
    setSkippedPolicy(source?.skippedPolicy ?? "NoRefund");
    setExtraPolicy(source?.extraPolicy ?? "ChargeSeparately");
    setMaxPerEpisode(String(source?.maxPerEpisode ?? 1));
    setIsActive(source?.isActive ?? true);
    setComponents(
      source && source.components.length > 0
        ? source.components.map(c => ({
            key: crypto.randomUUID(),
            lineType: c.lineType,
            service: savedServiceRow(c), serviceId: c.serviceId ?? "", serviceName: c.serviceName ?? "",
            item: savedItemRow(c), itemId: c.itemId ?? "", itemName: c.itemName ?? c.sku ?? "",
            qty: String(c.qty),
            listRate: c.listRate,
          }))
        : [blankComponent()]);
  }, [open, pkg, cloneFrom, source, isEdit]);

  const schemes = useQuery({
    queryKey: ["insurance-schemes", "options"],
    queryFn: () => insuranceSchemesApi.list({ isActive: true, pageSize: 200 }),
    staleTime: 5 * 60_000, enabled: open,
  });
  const currencies = useQuery({
    queryKey: ["currencies", "options"],
    queryFn: () => currenciesApi.list({ isActive: true, pageSize: 100 }),
    staleTime: 5 * 60_000, enabled: open,
  });
  const set = (key: string, patch: Partial<DraftComponent>) =>
    setComponents(cs => cs.map(c => (c.key === key ? { ...c, ...patch } : c)));

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Package updated" : "Package created" },
    mutationFn: async () => {
      const payload: UpsertPackageRequest = {
        code: code.trim(),
        name: name.trim(),
        description: description.trim() || null,
        packageType,
        price: Number(price || 0),
        currencyId: currencyId || null,
        insuranceSchemeId: insuranceSchemeId || null,
        effectiveFrom,
        effectiveTo: effectiveTo || null,
        skippedPolicy,
        extraPolicy,
        maxPerEpisode: Number(maxPerEpisode || 1),
        isActive,
        components: components.map((c, i) => ({
          lineType: c.lineType,
          serviceId: c.lineType === "Service" ? c.serviceId || null : null,
          itemId: c.lineType === "Service" ? null : c.itemId || null,
          qty: Number(c.qty || 0),
          sortOrder: i + 1,
        })),
      };
      if (isEdit) await packagesApi.update(pkg!.id, payload);
      else await packagesApi.create(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["packages"] });
      onClose();
    },
  });

  // What the basket is worth, where we know. Only meaningful for components loaded from a saved
  // package — a freshly picked one has no rate on screen until it is saved and read back.
  const knownValue = components.reduce(
    (sum, c) => sum + (c.listRate ?? 0) * Number(c.qty || 0), 0);
  const anyRatesKnown = components.some(c => c.listRate != null);
  const priceNum = Number(price || 0);

  const err =
    !code.trim() ? "Code required"
    : !name.trim() ? "Name required"
    : priceNum < 0 ? "A package price cannot be negative"
    : Number(maxPerEpisode || 0) < 1 ? "A package has to be billable at least once per episode"
    : effectiveTo && effectiveTo < effectiveFrom ? "A package cannot end before it starts"
    : components.length === 0 ? "A package has to include something"
    : components.some(c => Number(c.qty || 0) <= 0) ? "Every component needs a quantity above zero"
    : components.some(c => (c.lineType === "Service" ? !c.serviceId : !c.itemId))
      ? "Every component needs a service or an item"
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err) mutation.mutate(); }}>
        <ClosableDialogTitle onClose={onClose}>
          {isEdit ? `Edit ${pkg!.code}` : cloneFrom ? `New package from ${cloneFrom.code}` : "New package"}
        </ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Code *" size="small" fullWidth autoFocus
                value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                disabled={isEdit}
                helperText={isEdit ? "Code cannot be changed" : "e.g. HC-EXEC"} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Name *" size="small" fullWidth
                value={name} onChange={(e) => setName(e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField select label="Kind" size="small" fullWidth
                value={packageType} onChange={(e) => setPackageType(e.target.value as PackageType)}>
                {(Object.keys(TYPE_LABEL) as PackageType[]).map(t => (
                  <MenuItem key={t} value={t}>{TYPE_LABEL[t]}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={12}>
              <TextField label="Description" size="small" fullWidth multiline minRows={2}
                value={description} onChange={(e) => setDescription(e.target.value)}
                inputProps={{ maxLength: 1000 }} />
            </Grid>

            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField type="number" label="Package price *" size="small" fullWidth
                value={price} onChange={(e) => setPrice(e.target.value)}
                inputProps={{ min: 0, step: "0.01" }}
                helperText="What the patient pays for the lot" />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField select label="Currency" size="small" fullWidth
                value={currencyId} onChange={(e) => setCurrencyId(e.target.value)}>
                <MenuItem value="">— Base —</MenuItem>
                {(currencies.data?.items ?? []).map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.code}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select label="Offered to" size="small" fullWidth
                value={insuranceSchemeId} onChange={(e) => setInsuranceSchemeId(e.target.value)}
                helperText="Blank = anybody. Set it and only that scheme's patients can be sold it.">
                <MenuItem value="">— Everyone —</MenuItem>
                {(schemes.data?.items ?? []).map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.code} — {s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField type="date" label="On offer from *" size="small" fullWidth
                value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField type="date" label="Until" size="small" fullWidth
                value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)}
                InputLabelProps={{ shrink: true }} helperText="Blank = open-ended" />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField type="number" label="Max per episode" size="small" fullWidth
                value={maxPerEpisode} onChange={(e) => setMaxPerEpisode(e.target.value)}
                inputProps={{ min: 1, max: 100 }}
                helperText="Admission for IP, one bill for OP" />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField select label="Status" size="small" fullWidth
                value={isActive ? "1" : "0"} onChange={(e) => setIsActive(e.target.value === "1")}>
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">Inactive</MenuItem>
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select label="If something is not delivered" size="small" fullWidth
                value={skippedPolicy}
                onChange={(e) => setSkippedPolicy(e.target.value as SkippedComponentPolicy)}
                helperText={skippedPolicy === "NoRefund"
                  ? "The price stands — the basket is priced as a whole"
                  : "The price drops by that part's share of the basket"}>
                <MenuItem value="NoRefund">Price stands</MenuItem>
                <MenuItem value="ProRataCredit">Credit its share back</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select label="If more is delivered" size="small" fullWidth
                value={extraPolicy}
                onChange={(e) => setExtraPolicy(e.target.value as ExtraComponentPolicy)}
                helperText={extraPolicy === "ChargeSeparately"
                  ? "The excess bills at its ordinary rate"
                  : "The excess is covered by the package price"}>
                <MenuItem value="ChargeSeparately">Bill it separately</MenuItem>
                <MenuItem value="IncludedNoCharge">Covered — no extra charge</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="overline" color="text.secondary">What it includes</Typography>
            {anyRatesKnown && (
              <Typography variant="caption" color={knownValue < priceNum ? "warning.main" : "text.secondary"}>
                Components are worth {money(knownValue)} separately
                {knownValue > 0 && priceNum > 0 && knownValue >= priceNum
                  ? ` — a saving of ${money(knownValue - priceNum)}`
                  : knownValue > 0 && priceNum > knownValue
                  ? " — the package costs MORE than its parts"
                  : ""}
              </Typography>
            )}
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 130 }}>Bills as</TableCell>
                <TableCell sx={{ minWidth: 280 }}>Service / item *</TableCell>
                <TableCell align="right" sx={{ width: 110 }}>Included qty *</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>Worth</TableCell>
                <TableCell sx={{ width: 60 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {components.map(c => (
                <TableRow key={c.key}>
                  <TableCell>
                    <TextField select size="small" fullWidth value={c.lineType}
                      onChange={(e) => set(c.key, {
                        lineType: e.target.value as DraftComponent["lineType"],
                        // The picked row belongs to the other catalog, so it goes.
                        service: null, serviceId: "", item: null, itemId: "", listRate: null,
                      })}>
                      <MenuItem value="Service">Service</MenuItem>
                      <MenuItem value="Item">Item</MenuItem>
                      <MenuItem value="Rx">Prescription</MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell>
                    {c.lineType === "Service" ? (
                      <ServicePicker
                        value={c.service} label={null}
                        onChange={(v) =>
                          set(c.key, { service: v, serviceId: v?.id ?? "", serviceName: v?.name ?? "" })}
                        placeholder={c.serviceName || "Search service…"}
                      />
                    ) : (
                      <CatalogItemPicker
                        value={c.item}
                        onChange={(v) =>
                          set(c.key, { item: v, itemId: v?.id ?? "", itemName: v?.name ?? "" })}
                        placeholder={c.itemName || "Search item…"}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" sx={{ width: 100 }}
                      value={c.qty} onChange={(e) => set(c.key, { qty: e.target.value })}
                      inputProps={{ min: 0, step: "1", style: { textAlign: "right" } }} />
                  </TableCell>
                  <TableCell align="right">
                    {c.listRate == null ? (
                      <Tooltip title="Known once the package is saved and read back">
                        <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
                      </Tooltip>
                    ) : (
                      money(c.listRate * Number(c.qty || 0))
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small"
                      onClick={() => setComponents(cs => cs.filter(x => x.key !== c.key))}
                      disabled={components.length === 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button startIcon={<AddIcon />} size="small" sx={{ mt: 1 }}
            onClick={() => setComponents(cs => [...cs, blankComponent()])}>
            Add component
          </Button>

          {err && <Alert severity="info" sx={{ mt: 2 }}>{err}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
